import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Lightbulb,
  Zap,
  Shield,
  Award,
  Download,
  Play,
  ChevronRight,
  Star,
  CheckCircle,
  ArrowRight,
  Globe,
  Battery,
  Settings,
  Eye,
} from "lucide-react";

import ledlightsbanner from "../img/services/led-lights/led-lights-banner.jpg";
import ledlightsmanufacturing from "../img/services/led-lights/led-lights-manufacturing.jpg";
import ledmachinery from "../img/services/led-lights/led-machinery.jpg";
import ledlightsspinoff1 from "../img/services/led-lights/led-lights-spinoff1.jpg";
import ledlightsspinoff2 from "../img/services/led-lights/led-lights-spinoff2.jpg";
import ledlightsspinoff3 from "../img/services/led-lights/led-lights-spinoff3.jpg";
import ledlightsspinoff4 from "../img/services/led-lights/led-lights-spinoff4.jpg";
import ledlightsspinoff5 from "../img/services/led-lights/led-lights-spinoff5.jpg";
import ledlightsspinoff6 from "../img/services/led-lights/led-lights-spinoff6.jpg";
import ledlightsspinoff7 from "../img/services/led-lights/led-lights-spinoff7.jpg";
import ledlightsspinoff8 from "../img/services/led-lights/led-lights-spinoff8.jpg";
import ledlightsspinoff9 from "../img/services/led-lights/led-lights-spinoff9.jpg";
import ledlightsspinoff10 from "../img/services/led-lights/led-lights-spinoff10.jpg";

import LedLightsCatalogue1 from "../catalogues/ledlights/catalogue1-1.pdf";
import LedLightsCatalogue2 from "../catalogues/ledlights/catalogue2-1.pdf";
import LedLightsCatalogue3 from "../catalogues/ledlights/catalogue3-1.pdf";
import LedLightsCatalogue4 from "../catalogues/ledlights/catalogue4-1.pdf";
import LedLightsCatalogue5 from "../catalogues/ledlights/catalogue5-1.pdf";
import FancyCatalogue from "../catalogues/ledlights/fancy-lights-catalogue.pdf";

import secondimg from '../img/spinoffimg/secondimg.png';

import ledvideo from "../video/ledvideo.mp4";

function Ledlights() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % 3);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const products = [
    {
      title: "Architectural Lighting",
      description:
        "Combining aesthetics with functionality to enhance space design and ambiance",
      icon: Settings,
      gradient: "from-blue-500 to-indigo-600",
    },
    {
      title: "Lighting Automation",
      description:
        "Smart control systems for enhanced energy efficiency and convenience",
      icon: Zap,
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      title: "COB Lights",
      description:
        "Compact and powerful lighting solutions for industrial applications",
      icon: Battery,
      gradient: "from-amber-500 to-orange-600",
    },
    {
      title: "Outdoor Lights",
      description:
        "Rugged streetlights to elegant garden fixtures for safety and appeal",
      icon: Eye,
      gradient: "from-violet-500 to-purple-600",
    },
    {
      title: "Pharma Lights",
      description:
        "Specialized lighting for pharmaceutical facilities and clean rooms",
      icon: Shield,
      gradient: "from-rose-500 to-pink-600",
    },
    {
      title: "Highbay Lights",
      description:
        "Superior brightness for warehouses, factories, and large indoor spaces",
      icon: Lightbulb,
      gradient: "from-cyan-500 to-blue-600",
    },
  ];

  const catalogues = [
    { name: "Imperial Lights Catalogue - 1", file: LedLightsCatalogue1 },
    { name: "Imperial Lights Catalogue - 2", file: LedLightsCatalogue2 },
    { name: "Imperial Lights Catalogue - 3", file: LedLightsCatalogue3 },
    { name: "Imperial Lights Catalogue - 4", file: LedLightsCatalogue4 },
    { name: "Imperial Lights Catalogue - 5", file: LedLightsCatalogue5 },
    { name: "Finike Lights - Decorative Lights", file: FancyCatalogue },
  ];

  return (
    <div className="home-container">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-cyan-400/20 to-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-br from-indigo-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section - Rich with Colors and Effects */}
      <section className="pt-32 pb-20 px-6 text-center relative overflow-hidden">
        {/* Rich Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-white to-blue-100 opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Enhanced Feature Pill with Gradient */}
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full text-sm font-medium text-white mb-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <Lightbulb className="w-5 h-5 text-yellow-300" />
            <span className="bg-gradient-to-r from-yellow-200 to-yellow-100 bg-clip-text text-transparent font-bold">
              Premium
            </span>{" "}
            LED Solutions
            <Zap className="w-4 h-4 text-yellow-300 animate-pulse" />
          </div>

          {/* Rich Main Headline with Multiple Gradients */}
          <h1 className="text-6xl md:text-6xl font-extrabold tracking-tighter mb-8 leading-tight">
            <span className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 bg-clip-text text-transparent">
              LED Lights
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Manufacturing
            </span>
          </h1>

          {/* Enhanced Subtext with Gradient */}
          <p className="text-2xl text-slate-600 mb-16 max-w-4xl mx-auto leading-relaxed bg-gradient-to-r from-slate-600 to-slate-500 bg-clip-text text-transparent">
            Redefining illumination with durable, modern LED solutions designed
            for lasting performance and efficiency in every application.
          </p>

          {/* Rich Stats with Gradient Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-20">
            <div className="group relative bg-gradient-to-br from-white to-blue-50 border border-blue-200/50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-5xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                  500+
                </div>
                <div className="text-slate-600 text-base font-semibold">
                  LED Products
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-white to-emerald-50 border border-emerald-200/50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-teal-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-5xl font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-3">
                  10+
                </div>
                <div className="text-slate-600 text-base font-semibold">
                  Years Experience
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-white to-rose-50 border border-rose-200/50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-400/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-5xl font-black bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-3">
                  99%
                </div>
                <div className="text-slate-600 text-base font-semibold">
                  Energy Efficient
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-rose-400 to-pink-600 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-white to-amber-50 border border-amber-200/50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-orange-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-5xl font-black bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-3">
                  Quick
                </div>
                <div className="text-slate-600 text-base font-semibold">
                  Support
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br from-amber-400 to-orange-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

   <section className="py-16 md:py-24 bg-gradient-to-b from-slate-950 via-slate-900 to-black relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute top-20 right-10 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl"></div>
    <div className="absolute bottom-10 left-10 w-80 h-80 bg-indigo-600/8 rounded-full blur-3xl"></div>
  </div>

  <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
    {/* Main container — no border, stronger shadow + backdrop */}
    <div className="bg-gradient-to-b from-slate-900/92 to-slate-950/92 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 sm:p-8 lg:p-10 xl:p-12 shadow-2xl shadow-black/60">
      
      {/* Top headline area */}
      <div className="text-center mb-10 md:mb-14">
        <div className="inline-flex items-center gap-3 px-7 py-3 bg-indigo-950/60 rounded-full text-indigo-300 font-semibold tracking-wider uppercase text-sm mb-6">
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-400 animate-pulse"></span>
          LED MANUFACTURING
        </div>

        <h2 className="text-4xl sm:text-5xl lg:text-5xl font-extrabold tracking-tight mb-5 leading-tight text-white">
          Exploring Spinoff's{" "}
          <span className="highlight-gradient">LED Lighting Solutions</span>
        </h2>

        <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
          At Spinoff Technopark Ltd., we are dedicated to illuminating the future with cutting-edge LED lighting 
          solutions that redefine efficiency, durability, and innovation. As a leader in the manufacturing industry, 
          we specialize in a diverse range of LED lighting products tailored to meet the dynamic needs of various sectors.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 xl:gap-10">

        {/* Left column */}
        <div className="lg:col-span-12 space-y-8 lg:space-y-10 lg:space-y-12">

          {/* Facility image — no border */}
          <div className="relative rounded-xl lg:rounded-2xl overflow-hidden shadow-xl lg:shadow-2xl group">
            <img
              src={secondimg}
              alt="Advanced LED Manufacturing"
              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent flex items-end p-4 lg:p-6">
              <div>
                <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-2">
                  Advanced Manufacturing Facility
                </h3>
                <p className="text-slate-200 text-sm lg:text-base leading-relaxed max-w-2xl">
                  Our state-of-the-art facility combines precision engineering with innovative technology to produce world-class LED solutions — every product passes rigorous quality testing.
                </p>
              </div>
            </div>
          </div>

          {/* Stats — no border, cleaner look */}
          <div className="grid grid-cols-3 gap-4 sm:gap-6" id="ledstats">
            {[
              { value: "500K+", label: "Units Produced Monthly", color: "indigo" },
              { value: "ISO 9001", label: "Certified Quality", color: "emerald" },
              { value: "50K+", label: "Happy Clients", color: "purple" },
            ].map((stat, i) => (
              <div
                key={i}
                className={`text-center p-5 sm:p-6 rounded-xl bg-gradient-to-br from-${stat.color}-950/50 to-slate-950/50 shadow-md`}
              >
                <div className={`text-3xl sm:text-4xl font-black text-${stat.color}-400 mb-1.5`}>
                  {stat.value}
                </div>
                <div className="text-slate-300 text-sm sm:text-base font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* Right column */}
        <div className="lg:col-span-12 space-y-8 lg:space-y-10 lg:space-y-12">

          {/* Product Categories — no border on cards */}
          <div>
            <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-4 lg:mb-6 text-center">
              Our Product Categories
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
              {products.map((product, idx) => {
                const Icon = product.icon;
                return (
                  <div
                    key={idx}
                    className="group p-4 sm:p-5 lg:p-6 rounded-xl bg-slate-800/40 hover:bg-slate-800/60 transition-all duration-300 flex flex-col items-center text-center shadow-md lg:shadow-lg"
                  >
                    <div
                      className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br ${product.gradient} rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform shadow-lg`}
                    >
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-white text-sm sm:text-base lg:text-lg mb-2">
                      {product.title}
                    </h4>
                    <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Product Catalogues Block */}
      <section
        className="brands"
        style={{ padding: "100px 0", background: "#fafafa" }}
      >
        <div className="max-w">
          <div
            className="section-head"
            style={{ marginBottom: "60px", textAlign: "center" }}
          >
            <div
              style={{
                display: "inline-flex",
                padding: "12px",
                background: "rgba(99,102,241,0.05)",
                borderRadius: "16px",
                color: "#6366f1",
                marginBottom: "20px",
              }}
            >
              <Download size={32} />
            </div>
            <h2
              style={{
                fontSize: "40px",
                fontWeight: "900",
                letterSpacing: "-1.5px",
              }}
            >
              Product <span className="highlight-gradient">Catalogues.</span>
            </h2>
            <p
              style={{ color: "#71717a", maxWidth: "600px", margin: "0 auto" }}
            >
              Download our comprehensive brochures to explore our complete range
              of LED lighting products.
            </p>
          </div>

          <div className="brands-grid">
            {catalogues.map((catalogue, index) => (
              <div key={index} className="brand-card-3d">
                <a
                  href={catalogue.file}
                  target="_blank"
                  className="block group"
                >
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <Download className="w-10 h-10 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">
                      {catalogue.name}
                    </h4>
                    <p className="text-gray-600 text-sm mb-4">PDF Catalogue</p>
                    <div className="flex items-center justify-center gap-2 text-blue-600 font-semibold">
                      Download <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Block */}
      <section className="who-we-are py-20 md:py-28 bg-gradient-to-b from-black to-slate-950 relative">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-indigo-950/40 border border-indigo-700/30 rounded-full text-indigo-300 font-semibold tracking-wider uppercase mb-6">
              <CheckCircle size={24} className="text-green-400" />
              Why Choose Us
            </div>

            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4" style={{color:'white'}}>
              Why <span className="highlight-gradient">Choose Us</span>.
            </h2>

            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Discover the advantages that make Spinoff Technopark the preferred
              choice for LED lighting solutions.
            </p>
          </div>

          <div className="bento-card bento-card-main bg-gradient-to-b from-slate-900/80 to-slate-950/90 backdrop-blur-sm border border-slate-800/50 rounded-2xl md:rounded-3xl p-8 md:p-10 lg:p-12 shadow-2xl max-w-5xl mx-auto">
            <div className="bento-orb absolute -top-20 -right-20 w-64 h-64 bg-purple-600/15 rounded-full blur-3xl" />

            {/* Centered Heading + Description */}
            <div className="text-center mb-10 md:mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-5">
                Leading LED Innovation
              </h3>
              <p className="text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto">
                Choose Spinoff Technopark for cutting-edge LED solutions:
                unmatched efficiency, durability, and tailored designs.
                Transform spaces with sustainable, high-performance lighting
                trusted worldwide.
              </p>
            </div>

            {/* Features Grid - already centered */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center p-7 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-green-500/40 transition-all duration-300">
                <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-5" />
                <h4 className="text-white font-semibold text-xl mb-3">
                  Premium Quality
                </h4>
                <p className="text-slate-400 text-base">
                  100% quality testing and international standards
                </p>
              </div>

              <div className="text-center p-7 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-green-500/40 transition-all duration-300">
                <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-5" />
                <h4 className="text-white font-semibold text-xl mb-3">
                  Energy Efficient
                </h4>
                <p className="text-slate-400 text-base">
                  Save up to 80% on energy costs
                </p>
              </div>

              <div className="text-center p-7 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-green-500/40 transition-all duration-300">
                <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-5" />
                <h4 className="text-white font-semibold text-xl mb-3">
                  Custom Design
                </h4>
                <p className="text-slate-400 text-base">
                  Tailored solutions for your needs
                </p>
              </div>
            </div>

    
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section
        className="brands"
        style={{ padding: "100px 0", background: "#fafafa" }}
      >
        <div className="max-w">
          <div
            className="section-head"
            style={{ marginBottom: "60px", textAlign: "center" }}
          >
            <div
              style={{
                display: "inline-flex",
                padding: "12px",
                background: "rgba(99,102,241,0.05)",
                borderRadius: "16px",
                color: "#6366f1",
                marginBottom: "20px",
              }}
            >
              <Lightbulb size={32} />
            </div>
            <h2
              style={{
                fontSize: "40px",
                fontWeight: "900",
                letterSpacing: "-1.5px",
              }}
            >
              Our LED{" "}
              <span className="highlight-gradient">Product Gallery.</span>
            </h2>
            <p
              style={{ color: "#71717a", maxWidth: "600px", margin: "0 auto" }}
            >
              Explore our extensive range of LED lighting solutions designed for
              various applications.
            </p>
          </div>

          <div className="brands-grid">
            {[
              ledlightsspinoff1,
              ledlightsspinoff2,
              ledlightsspinoff3,
              ledlightsspinoff4,
              ledlightsspinoff5,
              ledlightsspinoff6,
              ledlightsspinoff7,
              ledlightsspinoff8,
              ledlightsspinoff9,
       
            ].map((image, index) => (
              <div key={index} className="brand-card-3d">
                <img
                  src={image}
                  alt={`LED Product ${index + 1}`}
                  className="w-full h-48 object-cover rounded-2xl"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-footer" style={{ padding: "100px 0" }}>
        <div className="max-w">
          <div className="cta-box">
            <h2 style={{fontSize:'55px'}}>
              Transform Your Space with{" "}
              <span className="highlight-gradient">LED Innovation.</span>
            </h2>
            <p>
              Connect with our lighting experts to discover the perfect LED
              solutions for your needs.
            </p>
            <button
              className="btn-3d btn-3d-primary"
              style={{ margin: "0 auto", background: "#fff", color: "#000" }}
            >
             <a href="/contact-us" style={{ color: "#000",target:"_blank"}}>Get Quote</a> <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Ledlights;
